'use client';

import { useState, useEffect, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ChevronDown, Gift, Building2, Ticket } from 'lucide-react';
import { LegalModal } from './legal-modal';

import { PHONE_COUNTRIES, getSelectedPhoneCountry, parseStoredPhone } from '@/lib/phone';
import { getCountryOptions, getFrequentCountryOptions } from '@/lib/countries';

// Re-exported for existing importers (checkout-form, checkout-client) that have
// long imported these from the guest form. The implementations live in
// `@/lib/phone` so the pure helpers are node-testable.
export { getSelectedPhoneCountry, parseStoredPhone };

export interface GuestFormData {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  locality: string;
  county: string;
  address: string;
  guestComment: string;
  termsAccepted: boolean;
  dealsOptIn: boolean;
  promoCode: string;
  wantsInvoice: boolean;
  companyName: string;
  cui: string;
  regCom: string;
  companyAddress: string;
  // Vacation voucher payer info. `usingVacationVoucher` is the section's
  // opt-in checkbox (mirrors the wantsInvoice pattern). When the user
  // clicks the "Vacation Vouchers" payment button without ticking this
  // first, checkout-form auto-ticks it so the section expands and the
  // validator can surface a focused error. `cnp` belongs to the voucher
  // holder (= guest when voucherHolderDifferent is false; = payer
  // otherwise). `payer*` fields only matter when voucherHolderDifferent.
  usingVacationVoucher: boolean;
  voucherHolderDifferent: boolean;
  cnp: string;
  payerName: string;
  payerEmail: string;
  payerPhone: string;
  payerAddress: string;
  // Gift voucher opt-in. Ticking this routes the booking through the
  // manual/staff-verified flow (no EuPlatesc, no bank transfer). The
  // code is required and persisted on the booking but is NOT validated
  // against `gift_vouchers` at submit time — staff verifies offline.
  usingGiftVoucher: boolean;
  giftVoucherCode: string;
}

export const INITIAL_GUEST_FORM: GuestFormData = {
  name: '',
  email: '',
  phone: '',
  countryCode: 'RO',
  locality: '',
  county: '',
  address: '',
  guestComment: '',
  termsAccepted: false,
  dealsOptIn: false,
  promoCode: '',
  wantsInvoice: false,
  companyName: '',
  cui: '',
  regCom: '',
  companyAddress: '',
  usingVacationVoucher: false,
  voucherHolderDifferent: false,
  cnp: '',
  payerName: '',
  payerEmail: '',
  payerPhone: '',
  payerAddress: '',
  usingGiftVoucher: false,
  giftVoucherCode: '',
};

const ROMANIAN_COUNTIES = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
  'Brașov', 'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj',
  'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj',
  'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți',
  'Mureș', 'Neamț', 'Olt', 'Prahova', 'Sălaj', 'Satu Mare', 'Sibiu', 'Suceava',
  'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea', 'Vrancea',
] as const;

const BUCHAREST_SECTORS = ['Sector 1', 'Sector 2', 'Sector 3', 'Sector 4', 'Sector 5', 'Sector 6'] as const;

function isSectorValue(v: string): boolean {
  return BUCHAREST_SECTORS.some(s => s === v);
}

interface CheckoutGuestFormProps {
  formData: GuestFormData;
  onFormChange: (data: GuestFormData) => void;
  phoneCountry: string;
  onPhoneCountryChange: (code: string) => void;
  onValidationClear: () => void;
  inputClass: string;
}

export function CheckoutGuestForm({
  formData,
  onFormChange,
  phoneCountry,
  onPhoneCountryChange,
  onValidationClear,
  inputClass,
}: CheckoutGuestFormProps) {
  const t = useTranslations('checkout');
  const locale = useLocale();
  const countryOptions = getCountryOptions(locale);
  const frequentCountries = getFrequentCountryOptions(locale);
  const [phoneDropdownOpen, setPhoneDropdownOpen] = useState(false);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);
  const [legalModal, setLegalModal] = useState<'privacy' | 'cookies' | 'terms' | null>(null);

  const selectedPhoneCountry = getSelectedPhoneCountry(phoneCountry);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (phoneDropdownRef.current && !phoneDropdownRef.current.contains(e.target as Node)) {
        setPhoneDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    onFormChange({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    onValidationClear();
  };

  return (
    <>
      {/* Guest Information */}
      <section className="mb-6">
        <h3 className="text-[#4e3529] font-semibold text-lg mb-4">{t('guestInfo.title')}</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {t('guestInfo.fullName')} <span className="text-[#801515]">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('guestInfo.namePlaceholder')}
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {t('guestInfo.email')} <span className="text-[#801515]">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('guestInfo.emailPlaceholder')}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="text-[#4e3529] font-medium text-sm block mb-2">
              {t('guestInfo.phone')} <span className="text-[#801515]">*</span>
            </label>
            <div ref={phoneDropdownRef} className="relative">
              <div className="flex items-center bg-gradient-to-br from-[#faf7f4] via-[#f6f1ec] to-[#f0e8e0] border border-[#4e3529]/18 text-[#2c1810] placeholder-[#4e3529]/35 focus-within:border-[#801515]/40 rounded-xl">
                <button
                  type="button"
                  onClick={() => setPhoneDropdownOpen(prev => !prev)}
                  className="flex items-center gap-1.5 pl-4 pr-2 py-3 shrink-0 cursor-pointer"
                >
                  <span className="text-base leading-none">{selectedPhoneCountry.flag}</span>
                  <span className="text-sm font-medium">{selectedPhoneCountry.dial}</span>
                  <ChevronDown className={`w-3 h-3 opacity-60 transition-transform duration-200 ${phoneDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className="w-px h-5 bg-current opacity-20 shrink-0" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={selectedPhoneCountry.placeholder}
                  className="flex-1 bg-transparent px-3 py-3 focus:outline-none min-w-0"
                />
              </div>
              {phoneDropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-gradient-to-br from-[#faf7f4] via-[#f6f1ec] to-[#f0e8e0] border border-[#4e3529]/12 rounded-xl overflow-hidden z-50 max-h-60 overflow-y-auto shadow-lg">
                  {PHONE_COUNTRIES.map(country => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => {
                        onPhoneCountryChange(country.code);
                        setPhoneDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 ${
                        phoneCountry === country.code
                          ? 'text-[#801515] font-medium'
                          : 'text-[#2c1810] hover:opacity-75'
                      }`}
                    >
                      <span className="text-base leading-none">{country.flag}</span>
                      <span className="text-sm flex-1">{country.name}</span>
                      <span className="text-xs text-[#4e3529]/65">{country.dial}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-4 ${formData.countryCode === 'RO' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {t('guestInfo.country')}
              </label>
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={(e) => {
                  const nextCountry = e.target.value;
                  const next: GuestFormData = { ...formData, countryCode: nextCountry };
                  if (nextCountry !== 'RO') {
                    next.county = '';
                    if (isSectorValue(formData.locality)) next.locality = '';
                  }
                  onFormChange(next);
                  onValidationClear();
                }}
                className={`${inputClass} select-chevron`}
              >
                {/* The frequent shortlist is duplicated inside the full list below.
                    A controlled <select> resolves `value` to the FIRST matching
                    option, so the pinned entry is the one shown — which is what we
                    want — and the duplicate is inert. */}
                <optgroup label={t('guestInfo.countryGroupFrequent')}>
                  {frequentCountries.map(c => (
                    <option key={`top-${c.code}`} value={c.code}>{c.name}</option>
                  ))}
                </optgroup>
                <optgroup label={t('guestInfo.countryGroupAll')}>
                  {countryOptions.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            {formData.countryCode === 'RO' && (
              <div>
                <label className="text-[#4e3529] font-medium text-sm block mb-2">
                  {t('guestInfo.county')} <span className="text-[#801515]">*</span>
                </label>
                <select
                  name="county"
                  value={formData.county}
                  onChange={(e) => {
                    const nextCounty = e.target.value;
                    const next: GuestFormData = { ...formData, county: nextCounty };
                    const wasBucharest = formData.county === 'București';
                    const isBucharest = nextCounty === 'București';
                    if (wasBucharest !== isBucharest) next.locality = '';
                    onFormChange(next);
                    onValidationClear();
                  }}
                  className={`${inputClass} select-chevron`}
                >
                  <option value="">{t('guestInfo.countyPlaceholder')}</option>
                  {ROMANIAN_COUNTIES.map(county => (
                    <option key={county} value={county}>{county}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {formData.county === 'București' ? t('guestInfo.sector') : t('guestInfo.locality')}{' '}
                <span className="text-[#801515]">*</span>
              </label>
              {formData.county === 'București' ? (
                <select
                  name="locality"
                  value={isSectorValue(formData.locality) ? formData.locality : ''}
                  onChange={handleInputChange}
                  className={`${inputClass} select-chevron`}
                >
                  <option value="">{t('guestInfo.sectorPlaceholder')}</option>
                  {BUCHAREST_SECTORS.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="locality"
                  value={formData.locality}
                  onChange={handleInputChange}
                  placeholder={t('guestInfo.localityPlaceholder')}
                  className={inputClass}
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-[#4e3529] font-medium text-sm block mb-2">
              {t('guestInfo.address')} <span className="text-[#801515]">*</span>
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder={t('guestInfo.addressPlaceholder')}
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div>
            <label className="text-[#4e3529] font-medium text-sm block mb-2">
              {t('specialRequests.label')} <span className="text-[#4e3529]/45 font-normal">({t('specialRequests.optional')})</span>
            </label>
            <textarea
              name="guestComment"
              value={formData.guestComment}
              onChange={handleInputChange}
              placeholder={t('specialRequests.placeholder')}
              rows={3}
              maxLength={500}
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>
      </section>

      {/* Company Invoice */}
      <section className="mb-6">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="wantsInvoice"
            checked={formData.wantsInvoice}
            onChange={handleInputChange}
            className="mt-1 w-4 h-4 accent-[#801515] bg-transparent rounded"
          />
          <div className="text-sm text-[#4e3529] font-medium flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#801515]/60" />
            {t('invoice.wantInvoice')}
          </div>
        </div>

        {formData.wantsInvoice && (
          <div className="mt-4 ml-7 pl-4 border-l-2 border-[#801515]/15 space-y-4">
            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {t('invoice.companyName')} <span className="text-[#801515]">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder={t('invoice.companyNamePlaceholder')}
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[#4e3529] font-medium text-sm block mb-2">
                  {t('invoice.cui')} <span className="text-[#801515]">*</span>
                </label>
                <input
                  type="text"
                  name="cui"
                  value={formData.cui}
                  onChange={handleInputChange}
                  placeholder="RO12345678"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="text-[#4e3529] font-medium text-sm block mb-2">
                  {t('invoice.regCom')}
                </label>
                <input
                  type="text"
                  name="regCom"
                  value={formData.regCom}
                  onChange={handleInputChange}
                  placeholder="J40/1234/2020"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {t('invoice.companyAddress')} <span className="text-[#801515]">*</span>
              </label>
              <input
                type="text"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleInputChange}
                placeholder={t('invoice.companyAddressPlaceholder')}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </section>

      {/* Vacation Voucher */}
      <section className="mb-6">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="usingVacationVoucher"
            checked={formData.usingVacationVoucher}
            onChange={handleInputChange}
            className="mt-1 w-4 h-4 accent-[#801515] bg-transparent rounded"
          />
          <div className="text-sm text-[#4e3529] font-medium flex items-center gap-2">
            <Ticket className="w-4 h-4 text-[#801515]/60" />
            {t('vacationVoucher.usingVoucher')}
          </div>
        </div>

        {formData.usingVacationVoucher && (
          <div className="mt-4 ml-7 pl-4 border-l-2 border-[#801515]/15 space-y-4">
            <p className="text-xs text-[#4e3529]/70 leading-relaxed italic">
              {t('vacationVoucher.legalNotice')}
            </p>

            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                name="voucherHolderDifferent"
                checked={formData.voucherHolderDifferent}
                onChange={handleInputChange}
                className="mt-1 w-4 h-4 accent-[#801515] bg-transparent rounded"
              />
              <div className="text-sm text-[#4e3529] font-medium">
                {t('vacationVoucher.holderDifferent')}
              </div>
            </div>

            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {formData.voucherHolderDifferent
                  ? t('vacationVoucher.payerCnp')
                  : t('vacationVoucher.cnp')}{' '}
                <span className="text-[#801515]">*</span>
              </label>
              <input
                type="text"
                name="cnp"
                value={formData.cnp}
                onChange={handleInputChange}
                placeholder={t('vacationVoucher.cnpPlaceholder')}
                inputMode="numeric"
                maxLength={13}
                className={inputClass}
              />
            </div>

            {formData.voucherHolderDifferent && (
              <>
                <div>
                  <label className="text-[#4e3529] font-medium text-sm block mb-2">
                    {t('vacationVoucher.payerName')} <span className="text-[#801515]">*</span>
                  </label>
                  <input
                    type="text"
                    name="payerName"
                    value={formData.payerName}
                    onChange={handleInputChange}
                    placeholder={t('guestInfo.namePlaceholder')}
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#4e3529] font-medium text-sm block mb-2">
                      {t('vacationVoucher.payerEmail')}
                    </label>
                    <input
                      type="email"
                      name="payerEmail"
                      value={formData.payerEmail}
                      onChange={handleInputChange}
                      placeholder={t('guestInfo.emailPlaceholder')}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="text-[#4e3529] font-medium text-sm block mb-2">
                      {t('vacationVoucher.payerPhone')}
                    </label>
                    <input
                      type="tel"
                      name="payerPhone"
                      value={formData.payerPhone}
                      onChange={handleInputChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[#4e3529] font-medium text-sm block mb-2">
                    {t('vacationVoucher.payerAddress')} <span className="text-[#801515]">*</span>
                  </label>
                  <textarea
                    name="payerAddress"
                    value={formData.payerAddress}
                    onChange={handleInputChange}
                    placeholder={t('guestInfo.addressPlaceholder')}
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* Gift Voucher opt-in. Ticking this signals "I will pay with a
          gift voucher" — the payment section then collapses to a single
          "Reserve, staff will contact you" CTA, skipping EuPlatesc / bank
          transfer entirely. No voucher code collected here; staff verifies
          the code offline and finalizes the booking from /admin/bookings. */}
      <section className="mb-6">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="usingGiftVoucher"
            checked={formData.usingGiftVoucher}
            onChange={handleInputChange}
            className="mt-1 w-4 h-4 accent-[#801515] bg-transparent rounded"
          />
          <div className="text-sm text-[#4e3529] font-medium flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#801515]/60" />
            {t('giftVoucher.usingVoucher')}
          </div>
        </div>

        {formData.usingGiftVoucher && (
          <div className="mt-4 ml-7 pl-4 border-l-2 border-[#801515]/15 space-y-3">
            <p className="text-xs text-[#4e3529]/70 leading-relaxed italic">
              {t('giftVoucher.staffNote')}
            </p>
            <div>
              <label className="text-[#4e3529] font-medium text-sm block mb-2">
                {t('giftVoucher.code')} <span className="text-[#801515]">*</span>
              </label>
              <input
                type="text"
                name="giftVoucherCode"
                value={formData.giftVoucherCode}
                onChange={handleInputChange}
                placeholder={t('giftVoucher.codePlaceholder')}
                autoComplete="off"
                maxLength={64}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </section>

      {/* Promo code lives in formData.promoCode but is rendered by
          <PromoCodeField> in checkout-form.tsx (it needs live dates/plan/total
          context to validate + preview the discount), not here. */}

      {/* Terms & Conditions */}
      <section className="mb-6 space-y-3">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="termsAccepted"
            checked={formData.termsAccepted}
            onChange={handleInputChange}
            className="mt-1 w-4 h-4 accent-[#801515] bg-transparent rounded"
          />
          <div className="text-xs text-[#4e3529]/65 leading-relaxed">
            {t.rich('terms.agreementText', {
              terms: (chunks) => (
                <button
                  onClick={() => setLegalModal('terms')}
                  className="text-[#801515] underline hover:text-[#601010]"
                >
                  {chunks}
                </button>
              ),
              privacy: (chunks) => (
                <button
                  onClick={() => setLegalModal('privacy')}
                  className="text-[#801515] underline hover:text-[#601010]"
                >
                  {chunks}
                </button>
              ),
            })}
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="dealsOptIn"
            checked={formData.dealsOptIn}
            onChange={handleInputChange}
            className="mt-1 w-4 h-4 accent-[#801515] bg-transparent rounded"
          />
          <div className="text-xs text-[#4e3529]/65 leading-relaxed flex items-center gap-1.5">
            <Gift className="w-3 h-3 text-[#801515]/50 shrink-0" />
            {t('terms.dealsOptIn')}
          </div>
        </div>
      </section>

      {/* Legal Modal */}
      {legalModal && (
        <LegalModal
          page={legalModal}
          open={!!legalModal}
          onClose={() => setLegalModal(null)}
        />
      )}
    </>
  );
}

/** Validate guest form, returning first error or null if valid. */
export function validateGuestForm(
  formData: GuestFormData,
  phoneCountry: string,
  childConfigs: { age: number | null }[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, values?: any) => string,
  paymentMethod?: string,
): string | null {
  if (!formData.name.trim()) return t('validation.nameRequired');
  if (!formData.email.trim()) return t('validation.emailRequired');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return t('validation.emailInvalid');
  if (!formData.phone.trim()) return t('validation.phoneRequired');
  if (phoneCountry === 'RO') {
    const digits = formData.phone.replace(/[\s\-()]/g, '');
    if (!/^7\d{8}$/.test(digits) && !/^07\d{8}$/.test(digits)) {
      return t('validation.phoneInvalidRO');
    }
  }
  if (formData.countryCode === 'RO') {
    if (!formData.county.trim()) return t('validation.countyRequired');
    if (formData.county === 'București') {
      if (!isSectorValue(formData.locality)) return t('validation.sectorRequired');
    } else if (!formData.locality.trim()) {
      return t('validation.localityRequired');
    }
  } else if (!formData.locality.trim()) {
    return t('validation.localityRequired');
  }
  if (!formData.address.trim()) return t('validation.addressRequired');
  for (let i = 0; i < childConfigs.length; i++) {
    if (childConfigs[i].age === null) return t('validation.childAgeRequired', { number: i + 1 });
  }
  if (formData.wantsInvoice) {
    if (!formData.companyName.trim()) return t('validation.companyNameRequired');
    if (!formData.cui.trim()) return t('validation.cuiRequired');
    if (!formData.companyAddress.trim()) return t('validation.companyAddressRequired');
  }
  // Vacation voucher: only validated when the user actually chose the
  // Vacation Vouchers payment method. The opt-in checkbox is just a UI
  // affordance for early disclosure — the voucher data is only "active"
  // (and persisted) when the matching payment button is clicked. Mirrored
  // server-side in normaliseVoucher: voucher data is dropped unless
  // paymentMethod === 'Vacation Vouchers'.
  const vacationVoucherSelected = paymentMethod === 'Vacation Vouchers';
  if (vacationVoucherSelected) {
    if (!formData.usingVacationVoucher) {
      return t('validation.vacationVoucherSectionRequired');
    }
    if (!formData.cnp.trim()) return t('validation.cnpRequired');
    // CNP is 13 digits in Romania. Be lenient on format but reject obvious junk.
    const cnpDigits = formData.cnp.replace(/\D/g, '');
    if (cnpDigits.length !== 13) return t('validation.cnpInvalid');
    if (formData.voucherHolderDifferent) {
      if (!formData.payerName.trim()) return t('validation.payerNameRequired');
      if (!formData.payerAddress.trim()) return t('validation.payerAddressRequired');
    }
  }
  // Gift voucher: only validated when the user actually chose the
  // Gift Voucher payment method (mirrors the vacation-voucher gate).
  // Code is required but free-text — staff verifies it offline.
  if (paymentMethod === 'Gift Voucher') {
    if (!formData.usingGiftVoucher) {
      return t('validation.giftVoucherSectionRequired');
    }
    if (!formData.giftVoucherCode.trim()) {
      return t('validation.giftVoucherCodeRequired');
    }
  }
  if (!formData.termsAccepted) return t('validation.termsRequired');
  return null;
}
